import type React from "react";
import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslations } from "use-intl";
import { getOAuthAuthorizeResumeUrl } from "@/auth/oauth-resume";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { useAppSession } from "@/lib/offline-session";
import { routeTemplates } from "@/lib/routes";
import { loginWithNext, safeNextFromSearch } from "@/lib/safe-next";
import { isPrerenderedPath } from "@/prerender/paths";

/**
 * Paths whose job is to get you INTO an account. A signed-in reader has no
 * business on them, so the gate sends them to the app instead.
 */
const AUTH_ENTRY_PATHS = [
  routeTemplates.login,
  routeTemplates.signup,
  routeTemplates.forgotPassword,
  routeTemplates.resetPassword,
];

/**
 * Paths a signed-out reader is allowed to open. `/verify-email` is public but
 * not an entry point: it is where Better Auth sends the browser once it has
 * verified an emailed link, so bouncing it would hide the result from the
 * host who clicked.
 */
const PUBLIC_PATHS = [...AUTH_ENTRY_PATHS, routeTemplates.verifyEmail];

function matchesPath(paths: string[], path: string): boolean {
  return paths.some((p) => path === p || path.startsWith(`${p}/`));
}

/** One-shot marker so a stale cached session can't loop the OAuth handoff. */
const OAUTH_RESUME_MARKER = "superhost.oauthResumeAttempted";

/**
 * Universal auth gate that wraps the entire app router.
 *
 * - Unauthenticated users on public routes (login, signup, …) are allowed through.
 * - Unauthenticated users on private routes are redirected to /login.
 * - Authenticated users on an auth ENTRY route are redirected to / (so you
 *   can't open /login when already logged in). `/verify-email` is public but
 *   NOT an entry route, so it renders signed in — that is the state the
 *   emailed link lands you in.
 * - Authenticated users on private routes are allowed through; downstream
 *   components (DefaultAppRedirect, OrgRouteGuard, PropertyRouteGuard)
 *   handle further gating.
 *
 * Redirects are performed inside useEffect to avoid React's
 * "Cannot update during an existing state transition" warning when
 * Ionic Router intercepts history changes.
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useAppSession();
  const location = useLocation();
  const history = useHistory();

  const onPublicRoute = matchesPath(PUBLIC_PATHS, location.pathname);
  const onAuthEntryRoute = matchesPath(AUTH_ENTRY_PATHS, location.pathname);
  const isLoggedIn = !!session?.user.id;

  const oauthResumeUrl = getOAuthAuthorizeResumeUrl(location.search);

  useEffect(() => {
    if (isPending) {
      return;
    }

    if (!(isLoggedIn || onPublicRoute)) {
      // Preserve where the reader was headed (e.g. a `/link/<target>` docs
      // deep link) as `?next=`, so sign-in returns them there instead of the
      // default landing screen. Same-origin paths only — `loginWithNext` drops
      // anything else.
      history.replace(
        loginWithNext(location.pathname + location.search + location.hash)
      );
    }
    if (isLoggedIn && onAuthEntryRoute) {
      // A signed-in user landing on /login with an OAuth authorize query is
      // mid-way through connecting an MCP client: leave the SPA and let the
      // API's authorize endpoint continue the flow (consent, then the
      // client's redirect_uri).
      //
      // Guard against a loop: `session` here can be an optimistic cached value
      // while the server cookie is actually expired. The API would then bounce
      // straight back to /login with the same query, and we'd redirect again
      // forever. A one-shot marker means we hand off to the API at most once;
      // if we're back on /login with the marker already set, fall through to
      // the sign-in form instead.
      if (oauthResumeUrl) {
        if (sessionStorage.getItem(OAUTH_RESUME_MARKER)) {
          sessionStorage.removeItem(OAUTH_RESUME_MARKER);
        } else {
          sessionStorage.setItem(OAUTH_RESUME_MARKER, "1");
          window.location.assign(oauthResumeUrl);
        }
        return;
      }
      // Honour a preserved `?next=` before defaulting to "/". Sign-in does its
      // own `history.replace(next)`, but the session can flip to logged-in a
      // tick later — long enough for the gate to bounce back to /login?next=…
      // and re-land here logged-in with next still in the URL. Sending them to
      // "/" would drop the destination. Same-origin
      // only (`safeNextFromSearch` rejects off-origin values).
      history.replace(safeNextFromSearch(location.search) ?? "/");
    }
  }, [
    isPending,
    isLoggedIn,
    onPublicRoute,
    onAuthEntryRoute,
    history,
    oauthResumeUrl,
    location.search,
    location.pathname,
    location.hash,
  ]);

  // A prerendered route is already on the reader's screen when the bundle
  // boots (ADR-0112), so covering it with a spinner would blank out markup
  // they are mid-sentence through and bring it back a moment later. Nothing is
  // withheld by rendering it: the document the build wrote is public, and a
  // signed-in reader is still bounced the tick the probe lands.
  //
  // Deliberately keyed on "was this prerendered", not "is this public". The
  // other public paths have nothing on screen to preserve, so exempting them
  // would only mean showing a live sign-in form during the probe — including
  // at `/login` mid-OAuth-handoff, where a click could navigate away and drop
  // the authorize query before the resume fires.
  if (isPending && !isPrerenderedPath(location.pathname)) {
    return <AuthLoading />;
  }

  // The two cases below are transient: the useEffect above will fire a
  // history.replace on the next tick. Render the spinner instead of `null`
  // so we never leave a literal blank screen behind if the redirect stalls
  // (e.g. router state race, auth API unreachable after the initial probe).
  if (!(isLoggedIn || onPublicRoute)) {
    return <AuthLoading />;
  }

  if (isLoggedIn && onAuthEntryRoute) {
    return <AuthLoading />;
  }

  return <>{children}</>;
}

function AuthLoading() {
  const t = useTranslations("shell");
  return <LoadingScreen caption={t("loading")} />;
}
